import React, { useEffect } from 'react'
import { ActionHistoryTableProps } from './ActionHistoryTable.types'
import styles from './ActionHistoryTable.module.scss'
import cl from 'classnames'
import {
  Button,
  HorizontalLine,
  NoneLabel,
  PaginationButtons,
  TableBodyContainer,
  TableBodyLine,
  TableContainer,
  TableHeader,
  Tag
} from 'components/shared'
import { observer } from 'mobx-react-lite'
import { inject } from 'mobx-react'
import {
  ActionHistoryTableSortProps,
  ACTION_HISTORY_TABLE_STORE,
  FilterPeriods,
  initialActionHistoryPaginationProps
} from 'store'
import { getActionTypeIcon } from './ActionHistoryTable.utils'
import moment from 'moment'
import { parseCamelCase, useGetScrollWidth } from 'utilities'
import tableBodyContainerStyle from '../../shared/TableBodyContainer/TableBodyContainer.module.scss'

const ActionHistoryTableComponent = (props: ActionHistoryTableProps) => {
  const {
    [ACTION_HISTORY_TABLE_STORE]: {
      getSortDirection,
      handleSortDirectionChange,
      searchProps,
      onFilterPeriodChange,
      onSearchPropsChange,
      isColumnMarked,
      actionTypeOptions,
      searchResults,
      paginationInfo,
      onPageSelect,
      triggerSearch,
      userId,
      setUserId,
      setPaginationProps
    },
    userId: userIdProp,
    hidePaginationInfo,
    paginationProps = initialActionHistoryPaginationProps,
    tableContainerProps,
    tableBodyLineProps,
    viewAllButtonContainerProps,
    viewAllButtonProps,
    isDashboardVersion
  } = props

  useEffect(() => {
    setUserId(userIdProp)
    setPaginationProps(paginationProps)
  }, [setUserId, userIdProp, setPaginationProps, paginationProps])
  useEffect(() => triggerSearch({ resetPageNumber: true }), [triggerSearch, userId])

  const { scrollX, handleScroll } = useGetScrollWidth()

  return (
    <TableContainer {...tableContainerProps} className={styles.thinScroll} onScroll={handleScroll}>
      <TableHeader
        tableColumns={[
          {
            label: 'period',
            className: styles.firstHeaderCol,
            sortDirection: getSortDirection(ActionHistoryTableSortProps.PERIOD),
            setSortDirection: handleSortDirectionChange(ActionHistoryTableSortProps.PERIOD),
            periodFilterProps: {
              period: searchProps.period,
              setPeriod: period => {
                onFilterPeriodChange(FilterPeriods.CUSTOM)
                onSearchPropsChange({ period })
              },
              className: styles.headerColDatePicker
            },
            // widthPercentage: 25,
            isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.PERIOD),
            widthPx: isDashboardVersion ? 439 : 450
          },
          {
            label: 'action type',
            sortDirection: getSortDirection(ActionHistoryTableSortProps.ACTION_TYPE),
            setSortDirection: handleSortDirectionChange(
              ActionHistoryTableSortProps.ACTION_TYPE
            ),
            selectProps: {
              options: actionTypeOptions,
              value: searchProps.action,
              onChange: action => onSearchPropsChange({ action }),
              noneLabel: 'All'
            },
            // widthPercentage: 25,
            isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.ACTION_TYPE),
            widthPx: isDashboardVersion ? 439 : 450
          },
          {
            label: 'entity title',
            sortDirection: getSortDirection(ActionHistoryTableSortProps.ENTITY_TITLE),
            setSortDirection: handleSortDirectionChange(
              ActionHistoryTableSortProps.ENTITY_TITLE
            ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.title,
              onChange: title => onSearchPropsChange({ title })
            },
            // widthPercentage: 25,
            isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.ENTITY_TITLE),
            widthPx: isDashboardVersion ? 438 : 455
          },
          {
            label: 'changes',
            sortDirection: getSortDirection(ActionHistoryTableSortProps.CHANGES),
            setSortDirection: handleSortDirectionChange(ActionHistoryTableSortProps.CHANGES),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.changes,
              onChange: changes => onSearchPropsChange({ changes })
            },
            // widthPercentage: 25,
            isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.CHANGES),
            widthPx: isDashboardVersion ? 438 : 455
          }
        ]}
      />
      <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
        {searchResults && searchResults
          .map((result, index) => {
            const isFirstLine = index === 0
            const isLastLine = index === searchResults.length - 1

            return (
              <TableBodyLine
                key={index}
                cells={[
                  {
                    // widthPercentage: 25,
                    isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.PERIOD),
                    children: (
                      <>
                        <div className={styles.iconContainer}>
                          <div
                            className={cl(styles.verticalLine, {
                              [styles.transparent]: isFirstLine
                            })}
                          />
                          <div className={styles.iconWrapper}>
                            {getActionTypeIcon(result.action)}
                          </div>
                          <div
                            className={cl(styles.verticalLine, {
                              [styles.transparent]: isLastLine
                            })}
                          />
                        </div>
                        {moment(result.createdOn).format('HH:mm, DD.MM.YYYY')}
                      </>
                    ),
                    widthPx: isDashboardVersion ? 439 : 450
                  },
                  {
                    // widthPercentage: 25,
                    isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.ACTION_TYPE),
                    children: parseCamelCase(result.action),
                    widthPx: isDashboardVersion ? 439 : 450
                  },
                  {
                    // widthPercentage: 25,
                    isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.ENTITY_TITLE),
                    children: result.entityTitle,
                    widthPx: isDashboardVersion ? 438 : 450
                  },
                  {
                    // widthPercentage: 25,
                    isColumnMarked: isColumnMarked(ActionHistoryTableSortProps.CHANGES),
                    children: (
                      <>
                        {(result.changes || []).map((field, index) =>
                          field && <Tag key={index} tag={field} />
                        )}
                      </>
                    ),
                    widthPx: isDashboardVersion ? 438 : 455
                  }
                ]}
                {...tableBodyLineProps}
              />
            )
          })}
      </TableBodyContainer>

      {!hidePaginationInfo && paginationInfo && (
        <PaginationButtons
          {...paginationInfo}
          onPageSelect={onPageSelect}
          style={ { width: scrollX } }
        />
      )}

      {searchResults.length
        ? Boolean(viewAllButtonProps) && (
          <div {...viewAllButtonContainerProps}>
            <HorizontalLine style={{ marginTop: 0 }} />
            <Button
              preset='link'
              style={{
                margin: '15px 5px'
              }}
              {...viewAllButtonProps}
            >
              View all
            </Button>
          </div>
        )
        : <div {...viewAllButtonContainerProps}>
          <NoneLabel style={{ margin: '15px 5px' }} />
        </div>
      }

    </TableContainer>
  )
}

const ActionHistoryTable = inject(
  'ACTION_HISTORY_TABLE_STORE'
)(observer(ActionHistoryTableComponent))
export { ActionHistoryTable }
